import { type ReactNode } from 'react'

import { cn } from '../lib/cn'

/** The three values `emergency_status` can hold. Structurally identical to the
 * unions in `api/emergency.ts` and `api/hospitals.ts`; named here so a consumer
 * does not have to reach into either. */
export type EmergencyStatusValue = 'open' | 'divert' | 'closed'

/**
 * Whether a hospital's emergency department is taking patients.
 *
 * EXTRACTED from `Emergency.tsx` by M19-T07, unchanged in wording or paint. It
 * moved because the Local home's hospital cards now show the same field, and
 * two screens describing one field in two vocabularies is the defect this
 * milestone keeps finding — the same reason M19-T03 copied `HospitalResults`'
 * filter line rather than writing a second one.
 *
 * All three values read differently. `divert` and `closed` share the warning
 * paint because both mean "do not simply turn up", but they are NOT collapsed
 * into one label: a department on divert is still open and still answering its
 * phone, and a patient deciding where to travel needs to know which they are
 * looking at. Colour is never the only signal — the label carries it, per
 * `CLAUDE.md`.
 */
export function EmergencyStatusBadge({
  status,
  className,
}: {
  status: EmergencyStatusValue
  className?: string
}): ReactNode {
  const label =
    status === 'open'
      ? 'Accepting'
      : status === 'divert'
        ? 'On divert'
        : 'ED closed'
  const good = status === 'open'
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium',
        good ? 'bg-bed-good-bg text-bed-good' : 'bg-bed-bad-bg text-bed-bad',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          good ? 'bg-bed-good' : 'bg-bed-bad',
        )}
      />
      {label}
    </span>
  )
}
